import { ScrollMarkRule } from "../../models/scroll/ScrollTriggerRule";
import { ScrollMode } from "../../states/ScrollState";
import { ScrollController } from "../controllers/ScrollController";
import { StringScrollDefault } from "../controllers/StringScrollDefault";
import { StringScrollDisable } from "../controllers/StringScrollDisable";
import { StringScrollSmooth } from "../controllers/StringScrollSmooth";
import { StringContext } from "../StringContext";
// import { logScrollDebug } from "../../utils/scroll-debug";

/**
 * Handles scroll engine setup and switching logic.
 * Synchronizes scroll modes with the centralized ScrollState inside StringContext.
 */
export class ScrollManager {
  private modes: Map<ScrollMode, ScrollController> = new Map();
  private boundEvents: {
    onScrollStart: () => void;
    onScrollStop: () => void;
    onDirectionChange: () => void;
    onModeChange: () => void;
  } | null = null;

  private scrollMarks: ScrollMarkRule[] = [];

  constructor(private context: StringContext) {
    this.registerMode("smooth", new StringScrollSmooth(context));
    this.registerMode("default", new StringScrollDefault(context));
    this.registerMode("disable", new StringScrollDisable(context));

    // Initial mode based on screen width
    this.updateResponsiveMode();
  }

  /**
   * Registers a new scroll mode with a given name and controller.
   * Can be used to overwrite existing modes or add custom ones.
   *
   * @param name The unique name of the scroll mode.
   * @param controller The ScrollController instance.
   */
  public registerMode(name: ScrollMode, controller: ScrollController): void {
    const isCurrentMode = this.context.data.scroll.mode === name;
    const existing = this.modes.get(name);

    if (existing) {
      if (isCurrentMode) {
        existing.deactivate();
      }
      existing.destroy();
    }

    if (!controller.name) {
      controller.name = String(name);
    }

    this.modes.set(name, controller);

    if (this.boundEvents) {
      controller.onScrollStart = this.boundEvents.onScrollStart;
      controller.onScrollStop = this.boundEvents.onScrollStop;
      controller.onChangeDirection = this.boundEvents.onDirectionChange;
    }

    if (this.scrollMarks.length > 0) {
      this.scrollMarks.forEach((rule) => controller.addScrollMark(rule));
    }

    if (isCurrentMode) {
      controller.activate();
    }
  }

  /**
   * Manually sets the scroll mode for mobile devices.
   * @param mode The scroll mode: 'smooth', 'default', or 'disable'.
   */
  public setMobileMode(mode: ScrollMode): void {
    this.context.data.scroll.modeMobile = mode;
    this.updateResponsiveMode();
  }

  /**
   * Manually sets the scroll mode for desktop devices.
   * @param mode The scroll mode: 'smooth', 'default', or 'disable'.
   */
  public setDesktopMode(mode: ScrollMode): void {
    this.context.data.scroll.modeDesktop = mode;
    this.updateResponsiveMode();
  }

  /**
   * Automatically switches scroll mode based on screen width.
   * Call this inside your resize handler.
   */
  public updateResponsiveMode(): void {
    const isMobile = window.innerWidth < 1024;
    const newMode = isMobile
      ? this.context.data.scroll.modeMobile
      : this.context.data.scroll.modeDesktop;

    this.setMode(newMode);
  }

  public updatePosition(): void {
    this.get().onCalcUpdate();
  }

  /**
   * Locks page scroll (sets mode to 'disable') without changing modeDesktop/modeMobile.
   * Use this when opening modals — scroll containers remain functional.
   */
  public lockPageScroll(): void {
    this.setMode("disable");
  }

  /**
   * Unlocks page scroll by restoring the responsive mode based on modeDesktop/modeMobile.
   */
  public unlockPageScroll(): void {
    this.updateResponsiveMode();
  }

  /**
   * Sets the current scroll mode and updates the active scroll engine.
   * @param mode The scroll mode to activate.
   */
  public setMode(mode: ScrollMode): void {
    if (!this.modes.has(mode)) {
      console.warn(`[ScrollManager] Unknown scroll mode: ${mode}`);
      return;
    }
    const currentMode = this.context.data.scroll.mode;

    if (currentMode === mode) {
      this.get().activate();
      return;
    }

    this.get().deactivate();
    this.context.data.scroll.mode = mode;
    this.get().activate();
    this.boundEvents?.onModeChange();
  }

  /**
   * Returns the currently active scroll engine based on state.
   */
  public get(): ScrollController {
    return this.modes.get(this.context.data.scroll.mode)!;
  }

  /**
   * Returns all available scroll engine instances.
   */
  public getEngines(): Map<ScrollMode, ScrollController> {
    return this.modes;
  }

  /**
   * Calls `onFrame()` on the current scroll engine.
   */
  public onFrame(): void {
    this.get().onFrame();
  }

  /**
   * Forwards native scroll event to the current scroll engine.
   * @param e The scroll event.
   */
  public onScroll(e: Event): void {
    this.get().onScroll(e);
  }

  /**
   * Forwards wheel event to the current scroll engine.
   * @param e The wheel event.
   */
  public onWheel(e: WheelEvent): void {
    this.get().onWheel(e);
  }

  /**
   * Forwards touchstart event to the current scroll engine.
   * @param e The touch event.
   */
  public onTouchStart(e: TouchEvent): void {
    this.get().onTouchStart(e);
  }

  /**
   * Forwards touchmove event to the current scroll engine.
   * @param e The touch event.
   */
  public onTouchMove(e: TouchEvent): void {
    this.get().onTouchMove(e);
  }

  /**
   * Forwards touchend event to the current scroll engine.
   * @param e The touch event.
   */
  public onTouchEnd(e: TouchEvent): void {
    this.get().onTouchEnd(e);
  }

  /**
   * Subscribes lifecycle event handlers to all scroll engines.
   * @param events Scroll lifecycle callbacks.
   */
  public bindEvents(events: {
    onScrollStart: () => void;
    onScrollStop: () => void;
    onDirectionChange: () => void;
    onModeChange: () => void;
  }) {
    this.boundEvents = events;
    this.modes.forEach((engine) => {
      engine.onScrollStart = events.onScrollStart;
      engine.onScrollStop = events.onScrollStop;
      engine.onChangeDirection = events.onDirectionChange;
    });
  }

  public addScrollMark(rule: ScrollMarkRule) {
    this.scrollMarks.push(rule);
    this.modes.forEach((engine) => {
      engine.addScrollMark(rule);
    });
  }
  public removeScrollMark(id: string) {
    this.scrollMarks = this.scrollMarks.filter((rule) => rule.id !== id);
    this.modes.forEach((engine) => {
      engine.removeScrollMark(id);
    });
  }

  public destroy(): void {
    this.modes.forEach((engine) => {
      engine.deactivate();
      engine.destroy();
    });
  }
}
